<template>
  <div>
    <modal id="mail-modal" :size="'lg'">
      <div slot="body">
        {{ selectedQuestion }}
      </div>
    </modal>
    <template v-if="isSmartThreading">
      <div>
        This version uses SMART threading. If you're interested in reviewing this project's Outline
        Report, please contact our team at support@capsim.com.
      </div>
    </template>
    <template v-else>
      <div id="visualisation" style="border: 1px black solid"></div>
    </template>
  </div>
</template>
<script>
  // import * as d3 from "d3";
  import mitchTree from 'd3-mitch-tree';
  import { mapState } from 'vuex';

  export default {
    name: 'OutlineChart',

    data() {
      return {
        treeEl: null,
        selectedQuestion: null,
      };
    },

    methods: {
      getKey(q) {
        if (!q.questionKey) return '';
        let typeId = this.questionGroups[q.FK_questionGroupKey]
          ? this.questionGroups[q.FK_questionGroupKey].name
          : q.questionKey;
        return typeId;
      },

      buildNode(q) {
        let retVal = {
          id: q.questionKey,
          name: this.getKey(q),
          description: q.subjectTagKey,
        };
        return retVal;
      },

      initializeTree(tree) {
        this.treeEl = new mitchTree.boxedTree()
          .setData(tree)
          .setAllowFocus(true)
          .setAllowZoom(true)
          .setAllowPan(true)
          .setElement(document.getElementById('visualisation'))
          .setIdAccessor((data) => data.id)
          .setChildrenAccessor((data) => data.children)
          .setBodyDisplayTextAccessor((data) => data.description)
          .setTitleDisplayTextAccessor((data) => data.name)
          .setOrientation('topToBottom')
          .getNodeSettings()
          .setSizingMode('nodesize')
          // .setBodyBoxHeight(100)
          .setVerticalSpacing(70)
          .setHorizontalSpacing(70)
          .back()
          .setMinScale(0.5)
          .initialize();
      },
    },

    watch: {
      treeData(val) {
        if (val) this.initializeTree(val);
      },
    },

    mounted() {
      if (this.treeData) this.initializeTree(this.treeData);
    },

    computed: {
      ...mapState('editMode/questions', ['questions', 'questionGroups']),
      ...mapState('editMode/version', ['versionName']),

      questionNodes() {
        return this.questionsArray.reduce((acc, q) => {
          acc[q.questionKey] = this.buildNode(q);
          return acc;
        }, {});
      },

      treeData() {
        let threadedKeys = [];

        Object.entries(this.threadedQuestions).forEach(([key, val]) => {
          this.questionNodes[key].children = Array.from(val).map((k) => this.questionNodes[k]);
          threadedKeys.push(...val);
        });

        let versionNode = {
          id: 'Version',
          name: 'Version',
          description: this.versionName,
          children: Object.values(this.questionNodes).filter((q) => !threadedKeys.includes(q.id)),
        };

        return versionNode;
      },

      threadedQuestions() {
        return this.questionsArray.reduce((acc, { questionKey, dependsOn }) => {
          for (let dep in dependsOn) {
            let originalQuestion = this.questionsArray.find((og) => og.answers[dep]);

            if (originalQuestion) {
              if (acc[originalQuestion.questionKey])
                acc[originalQuestion.questionKey].add(questionKey);
              else acc[originalQuestion.questionKey] = new Set([questionKey]);
            }
          }
          return acc;
        }, {});
      },

      questionsArray() {
        if (this.isSmartThreading) return [];
        return Object.values(this.questions);
      },

      isSmartThreading() {
        return Object.values(this.questions).some(({ isSmartThreading }) => isSmartThreading);
      },
    },
  };
</script>
<style lang="css">
  .mitch-d3-tree.boxed-tree .node {
    /*
				Offset the text so its right in the middle
				This code is included due to d3plus not 
				centering vertically correctly for the textbox
			*/
  }

  .mitch-d3-tree.boxed-tree .node .d3plus-textBox,
  .mitch-d3-tree.boxed-tree .node .body-group .body-box,
  .mitch-d3-tree.boxed-tree .node .title-group .title-box {
    cursor: pointer;
  }

  .mitch-d3-tree.boxed-tree .node .title-group .d3plus-textBox text {
    transform: translateY(0.15em);
  }

  .mitch-d3-tree.circle-tree .node circle {
    cursor: pointer;
    r: 0.4em;
  }

  .mitch-d3-tree.circle-tree .node text {
    transition: 0.3s linear;
  }

  .mitch-d3-tree.circle-tree .node.childless text {
    transform: translate(13px, 0.35em);
    text-anchor: start;
  }

  .mitch-d3-tree.circle-tree .node.collapsed text,
  .mitch-d3-tree.circle-tree .node.expanded text {
    transform: translate(-13px, 0.35em);
    text-anchor: end;
  }

  .mitch-d3-tree.circle-tree .node.middle.collapsed text,
  .mitch-d3-tree.circle-tree .node.middle.expanded text {
    transform: translate(-13px, -8px);
  }

  .mitch-d3-tree.boxed-tree.default .link {
    fill: none;
    stroke: #ccc;
    stroke-width: 2px;
  }

  .mitch-d3-tree.boxed-tree.default .node {
    /*
					Do NOT do any font related CSS that
					would affect dimensions here, as 
					D3Plus TextBox won't take them 
					into account when performing
					various operations such as ellipsis.
				*/
  }

  .mitch-d3-tree.boxed-tree.default .node.collapsed .body-group .body-box {
    fill: lightsteelblue;
  }

  .mitch-d3-tree.boxed-tree.default .node.selected .body-group .body-box {
    stroke: #00a2a3;
    stroke-width: 2.5px;
  }

  .mitch-d3-tree.boxed-tree.default .node .body-group .body-box {
    stroke: steelblue;
    fill: white;
    rx: 6;
    ry: 6;
  }

  .mitch-d3-tree.boxed-tree.default .node .title-group .title-box {
    stroke: steelblue;
    fill: #4682b4;
    rx: 10;
    ry: 10;
  }

  .mitch-d3-tree.boxed-tree.default .node .title-group text {
    fill: white;
  }

  .mitch-d3-tree.circle-tree.default .link {
    fill: none;
    stroke: #ccc;
    stroke-width: 2px;
  }

  .mitch-d3-tree.circle-tree.default .node.collapsed circle {
    fill: lightsteelblue;
  }

  .mitch-d3-tree.circle-tree.default .node.selected circle {
    stroke: #00a2a3;
    stroke-width: 2.5px;
  }

  .mitch-d3-tree.circle-tree.default .node circle {
    fill: #fff;
    stroke: steelblue;
    stroke-width: 3px;
  }

  .mitch-d3-tree.circle-tree.default .node text {
    font: 12px sans-serif;
  }

  #visualisation {
    height: 65vh;
  }
</style>
